/**
 * WaiveMissingInfoDialog — modal pour waiver une MI (Chunk 9.L.12).
 *
 * Backend : `PATCH /v1/missing-information/:id/waive` (9.I.2) avec gating
 * Couche 2 actif (`missing_information.waive` default `agent_allowed=false`).
 *
 * UX (parité strict 9.L.8 CancelQuoteRequestDialog) :
 *   - Textarea `waiver_reason` 1..500 chars requis.
 *   - Char counter live.
 *   - Submit disabled tant que reason vide ou > 500.
 *   - Warning amber forensic-preserve (audit_log capture "qui a waived").
 *   - Submit bouton red destructive (`bg-red-600`).
 *   - Esc + backdrop click → close (sauf pendant submit).
 *   - Focus initial sur Cancel.
 *
 * Erreurs API (bilingues) :
 *   - 404 MISSING_INFORMATION_NOT_FOUND               → bandeau red
 *   - 409 MISSING_INFORMATION_INVALID_TRANSITION      → bandeau red (status terminal)
 *   - 403 GATING_HUMAN_REQUIRED                       → bandeau red
 *   - 400 VALIDATION_ERROR (reason vide ou > 500)     → bandeau red
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useWaiveMissingInformation } from "../../../lib/queries/missingInformation";
import { toast } from "../../../lib/toast";

type Props = {
  readonly missingInformationId: string;
  readonly fieldLabel: string | null;
  readonly onClose: () => void;
};

const REASON_MAX = 500;

export function WaiveMissingInfoDialog({
  missingInformationId,
  fieldLabel,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const reasonFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const waive = useWaiveMissingInformation();
  const { reset: resetMutation } = waive;

  const [reason, setReason] = useState("");

  useEffect(() => {
    resetMutation();
    setReason("");
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !waive.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, waive.isPending]);

  const trimmed = reason.trim();
  const canSubmit =
    trimmed.length >= 1 && trimmed.length <= REASON_MAX && !waive.isPending;

  const onConfirm = () => {
    if (!canSubmit) return;
    waive.mutate(
      { missingInformationId, input: { waiver_reason: trimmed } },
      {
        onSuccess: () => {
          toast.success(t("missing_information.waive_dialog.success"));
          onClose();
        },
      },
    );
  };

  const apiErrorMsg =
    waive.error instanceof ApiException
      ? getApiErrorMessage(waive.error, lang) || t("errors.generic")
      : waive.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!waive.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("missing_information.waive_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {fieldLabel?.trim() || t("missing_information.waive_dialog.untitled")}
          </p>
          <p className="text-sm text-amber-700">
            {t("missing_information.waive_dialog.warning")}
          </p>

          <div>
            <label
              htmlFor={reasonFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("missing_information.waive_dialog.reason_label")}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </label>
            <textarea
              id={reasonFieldId}
              rows={4}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={waive.isPending}
              placeholder={t("missing_information.waive_dialog.reason_placeholder")}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
            <p className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
              <span>{t("missing_information.waive_dialog.reason_hint")}</span>
              <span>
                {trimmed.length}/{REASON_MAX}
              </span>
            </p>
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={waive.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canSubmit}
            aria-busy={waive.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {waive.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.25"
                />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {waive.isPending
              ? t("missing_information.waive_dialog.submitting")
              : t("missing_information.waive_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
